import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _get from "lodash/get";
import _merge from "lodash/merge";

var _typeMap;

import React, { Fragment, useContext } from 'react';
import { ImpressionContext } from '@nerdwallet/analytics/react/impression';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import Button from '@nerdwallet/react-button-short';
import { Link, Type } from '@nerdwallet/currency';
import { TrkElement } from '@nerdwallet/nw-client-lib/trk-helper';
import { buildLink } from './utils';
import { LINK_TYPES, TRK_PRODUCT_LOCATION } from './constants';
var typeMap = (_typeMap = {}, _defineProperty(_typeMap, LINK_TYPES.BUTTON, {
  Component: Button,
  trkElement: TrkElement.button
}), _defineProperty(_typeMap, LINK_TYPES.LINK, {
  Component: Link,
  trkElement: TrkElement.hyperlink
}), _typeMap);
/**
 * Display component for a NW standard "Read Review" link or button.
 */

var BaseReviewLink = function BaseReviewLink(_ref) {
  var children = _ref.children,
      className = _ref.className,
      href = _ref.href,
      onClick = _ref.onClick,
      primary = _ref.primary,
      shortButton = _ref.shortButton,
      size = _ref.size,
      subtext = _ref.subtext,
      text = _ref.text,
      trkLocation = _ref.trkLocation,
      trkPageType = _ref.trkPageType,
      trkSectionCategory = _ref.trkSectionCategory,
      trkTaxonomy = _ref.trkTaxonomy,
      type = _ref.type;
  var _typeMap$type = typeMap[type],
      Component = _typeMap$type.Component,
      trkElement = _typeMap$type.trkElement;
  var impressionContext = useContext(ImpressionContext);
  var navigationSourceImpressionId = impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId;

  if (!href) {
    return children || null;
  }

  var buttonProps = {};

  if (type === LINK_TYPES.BUTTON) {
    _merge(buttonProps, {
      // mirror primary CTA styling
      className: classNames(classes('width-100'), className),
      primary: primary,
      short: shortButton
    });
  }

  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Component, _extends({
    className: className,
    href: buildLink({
      navigationSourceImpressionId: navigationSourceImpressionId,
      url: href,
      trkElement: trkElement,
      trkPageType: trkPageType,
      trkLocation: trkLocation,
      trkSectionCategory: trkSectionCategory,
      trkTaxonomy: trkTaxonomy
    }),
    onClick: onClick,
    size: size,
    target: "_blank"
  }, buttonProps), children || text), subtext && type === LINK_TYPES.BUTTON && /*#__PURE__*/React.createElement(Type, {
    className: classes('margin-top-1', 'text-align-center'),
    color: "neutral-darker",
    size: 1
  }, subtext));
};

if (process.env.NODE_ENV !== 'production') {
  var PropTypes = require('prop-types'); // eslint-disable-line global-require


  BaseReviewLink.propTypes = {
    /** Children to render. If provided, children will render regardless of whether `href` is valid. */
    children: PropTypes.node,

    /** Classes to apply to the component. */
    className: PropTypes.string,

    /** Href for the review link. */
    href: PropTypes.string,

    /** Click handler to pass to the button */
    onClick: PropTypes.func,

    /** Button text. Defaults to `Read review` */
    text: PropTypes.string,

    /** Tracking page type */
    trkPageType: PropTypes.string,

    /** Tracking location */
    trkLocation: PropTypes.string,

    /** Tracking section category */
    trkSectionCategory: PropTypes.string,

    /** Tracking taxonomy */
    trkTaxonomy: PropTypes.shape({
      /** Taxonomy page type. */
      pageType: PropTypes.string,

      /** Taxonomy topic. */
      topic: PropTypes.string,

      /** Taxonomy vertical. */
      vertical: PropTypes.string
    }),

    /** The link type (button vs. link). Defaults to `LINK`. */
    type: PropTypes.oneOf([LINK_TYPES.BUTTON, LINK_TYPES.LINK]),
    // BUTTON specific props

    /** https://github.com/NerdWallet/react-components/blob/b0e6afb153b318a9c1afb4651139cff37b864fef/components/button/src/button.jsx#L79-L80 */
    primary: PropTypes.bool,

    /** Renders a visually shorter button */
    shortButton: PropTypes.bool,

    /** Subtext to include underneath the button */
    subtext: PropTypes.string,
    // LINK specific props

    /** Font size: 1=smallest, 5=biggest. Size is inherited if not specified. */
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  };
}

BaseReviewLink.defaultProps = {
  className: '',
  children: null,
  href: null,
  onClick: function onClick() {},
  text: 'Read review',
  trkLocation: TRK_PRODUCT_LOCATION,
  trkSectionCategory: null,
  trkTaxonomy: _get(global.window, 'nwPageTaxonomy'),
  trkPageType: null,
  type: LINK_TYPES.LINK,
  // BUTTON specific props
  primary: true,
  shortButton: false,
  subtext: null,
  // LINK specific props
  size: null
};
export default BaseReviewLink;